import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

interface DriveItem {
  id: string; // prefix or key
  name: string;
  isFolder: boolean;
  mimeType?: string;
  modifiedTime?: string;
  size?: number;
}

const formatBytes = (n?: number) => {
  if (!n && n !== 0) return '-';
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};

const ROOT_PREFIX = '';

interface TreeNode {
  name: string;
  prefix: string; // 'a/b/'
  expanded?: boolean;
  loading?: boolean;
  childrenLoaded?: boolean;
  children?: TreeNode[];
}

const StorageExplorerPage: React.FC = () => {
  const [currentPrefix, setCurrentPrefix] = useState(ROOT_PREFIX);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; item?: DriveItem | null }>({ visible: false, x: 0, y: 0, item: null });
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<{ open: boolean; name: string; url: string; contentType: string } | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string } | null>(null);
  const [expandMode, setExpandMode] = useState<'eager' | 'lazy'>('eager');
  const LAZY_DELAY_MS = 80;

  // 좌측 트리 상태
  const [treeRoot, setTreeRoot] = useState<TreeNode>({ name: '공유파일함', prefix: '', expanded: true, childrenLoaded: false, children: [] });

  // 브레드크럼 계산
  const breadcrumb = useMemo(() => {
    const parts = currentPrefix.split('/').filter(Boolean);
    const nodes: { name: string; prefix: string }[] = [{ name: '공유파일함', prefix: '' }];
    let acc = '';
    for (const p of parts) {
      acc += p + '/';
      nodes.push({ name: p, prefix: acc });
    }
    return nodes;
  }, [currentPrefix]);

  const fetchList = async (prefix: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/drive/list', { params: { id: prefix } });
      setItems(res.data.files || []);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message;
      setError(`목록을 불러오지 못했습니다.${detail ? `\n(${detail})` : ''}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(currentPrefix);
    // 트리 전체를 펼쳐서 구성
    rebuildFullTree();
  }, [currentPrefix]);

  // 좌측 트리용 폴더 로딩
  const fetchFolders = async (prefix: string): Promise<TreeNode[]> => {
    try {
      const res = await axios.get('/api/drive/list', { params: { id: prefix } });
      const folders = (res.data.files || []).filter((f: DriveItem) => f.isFolder);
      return folders.map((f: DriveItem) => ({ name: f.name, prefix: f.id, expanded: false, childrenLoaded: false, children: [] }));
    } catch {
      return [];
    }
  };

  const expandNode = async (node: TreeNode) => {
    if (node.loading) return;
    // 항상 펼쳐진 상태 유지
    node.expanded = true;
    if (!node.childrenLoaded) {
      node.loading = true;
      const children = await fetchFolders(node.prefix);
      node.children = children;
      node.childrenLoaded = true;
      node.loading = false;
    }
    setTreeRoot({ ...treeRoot });
  };

  // 전체 트리를 재구성하여 항상 모두 펼친 상태로 로드
  const rebuildFullTree = async () => {
    if (expandMode === 'lazy') {
      // 단계적 지연 로딩: 레벨별로 확장하며 약간의 지연을 둠
      const buildLevel = async (nodes: TreeNode[]): Promise<TreeNode[]> => {
        const result: TreeNode[] = [];
        for (const node of nodes) {
          const children = await fetchFolders(node.prefix);
          const newNode: TreeNode = {
            ...node,
            expanded: true,
            childrenLoaded: true,
            children: children.map(c => ({ ...c, expanded: true, childrenLoaded: false, children: [] }))
          };
          result.push(newNode);
        }
        return result;
      };
      // 루트 세팅
      let current: TreeNode = { name: '공유파일함', prefix: '', expanded: true, loading: false, childrenLoaded: true, children: [] };
      current.children = (await buildLevel([{ name: 'root', prefix: '' } as any]))[0].children || [];
      setTreeRoot({ ...current });
      // BFS식으로 단계 확장
      let queue: TreeNode[] = current.children || [];
      while (queue.length > 0) {
        const nextLevel: TreeNode[] = [];
        for (const node of queue) {
          const children = await fetchFolders(node.prefix);
          node.children = children.map(c => ({ ...c, expanded: true, childrenLoaded: true, children: [] }));
          nextLevel.push(...(node.children || []));
        }
        setTreeRoot(prev => ({ ...prev }));
        await new Promise(res => setTimeout(res, LAZY_DELAY_MS));
        queue = nextLevel;
      }
    } else {
      // 즉시 전부 확장 (기존 방식)
      const build = async (prefix: string, name: string): Promise<TreeNode> => {
        const children = await fetchFolders(prefix);
        const builtChildren: TreeNode[] = [];
        for (const child of children) {
          const built = await build(child.prefix, child.name);
          builtChildren.push(built);
        }
        return {
          name,
          prefix,
          expanded: true,
          loading: false,
          childrenLoaded: true,
          children: builtChildren,
        };
      };
      const root = await build('', '공유파일함');
      setTreeRoot(root);
    }
  };

  // 툴바 동작
  const handleUploadBtn = () => fileInputRef.current?.click();
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setToast({ open: true, message: '업로드 중...' });
      const form = new FormData();
      form.append('file', file);
      form.append('parentId', currentPrefix);
      await axios.post('/api/drive/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchList(currentPrefix);
      setToast({ open: true, message: '업로드 완료' });
      setTimeout(() => setToast(null), 1200);
    } catch (err) {
      alert('업로드 실패');
    } finally {
      e.target.value = '';
    }
  };

  // 드래그앤드롭 업로드
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    try {
      setLoading(true);
      let count = 0;
      for (const file of files) {
        setToast({ open: true, message: `업로드 중... (${++count}/${files.length})` });
        const form = new FormData();
        form.append('file', file);
        form.append('parentId', currentPrefix);
        await axios.post('/api/drive/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await fetchList(currentPrefix);
      setToast({ open: true, message: '업로드 완료' });
      setTimeout(() => setToast(null), 1200);
    } catch (err) {
      alert('드롭 업로드 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post('/api/drive/create-folder', { name: newFolderName.trim(), parentId: currentPrefix });
      setShowCreateFolder(false);
      setNewFolderName('');
      await fetchList(currentPrefix);
    } catch {
      alert('폴더 생성 실패');
    }
  };

  const handleDelete = async (ids?: string[]) => {
    const targets = ids && ids.length > 0 ? ids : selected;
    if (targets.length === 0) return;
    if (!window.confirm('선택 항목을 삭제하시겠습니까?')) return;
    try {
      setToast({ open: true, message: '삭제 중...' });
      await Promise.all(targets.map(id => axios.delete('/api/drive/delete', { params: { id } })));
      // 선택 해제 (지운 항목 제거)
      setSelected(prev => prev.filter(id => !targets.includes(id)));
      await fetchList(currentPrefix);
      setToast({ open: true, message: '삭제 완료' });
      setTimeout(() => setToast(null), 1200);
    } catch {
      alert('삭제 실패');
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const res = await fetch(`/api/drive/download?id=${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error('다운로드 실패');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = id.split('/').filter(Boolean).pop() || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('다운로드 실패');
    }
  };

  const handlePreview = async (id: string, name: string) => {
    try {
      const res = await fetch(`/api/drive/download?id=${encodeURIComponent(id)}`);
      if (!res.ok) throw new Error('미리보기 실패');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const contentType = res.headers.get('Content-Type') || '';
      setPreview({ open: true, name, url, contentType });
    } catch {
      alert('미리보기 실패');
    }
  };

  // 홈(루트)으로 이동하며 새로고침 역할 수행
  const handleGoHome = async () => {
    setCurrentPrefix('');
    await fetchList('');
    await rebuildFullTree();
  };

  const moveTargetRef = useRef<string>('');
  const handleMove = async () => {
    if (selected.length === 0) return;
    const target = prompt('이동할 경로 prefix를 입력하세요 (예: folder/sub/)\n루트로 이동하려면 빈 값으로 두세요.', '');
    if (target === null) return;
    moveTargetRef.current = target || '';
    try {
      await Promise.all(selected.map(fileId => axios.post('/api/drive/move', { fileId, targetParentId: moveTargetRef.current })));
      setSelected([]);
      await fetchList(currentPrefix);
    } catch {
      alert('이동 실패');
    }
  };

  const toggleSelect = (id: string, checked: boolean) => {
    setSelected(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 48px)' }}>
      {/* 좌측 폴더 트리 */}
      <div style={{ width: 280, borderRight: '1px solid #e5e7eb', padding: 12, overflow: 'auto' }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>폴더</div>
        <TreeView node={treeRoot} onToggle={expandNode} onSelect={(p) => setCurrentPrefix(p)} currentPrefix={currentPrefix} />
      </div>

      {/* 우측 패널 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* 툴바 */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* 통합/정산일보와 동일한 세그먼트형 버튼 그룹 */}
          <div style={{ display: 'flex', gap: 0 }}>
            <button
              onClick={() => setCurrentPrefix(breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2].prefix : '')}
              style={{
                background: '#fff', color: '#222', border: '1px solid #d1d5db', borderRight: 'none',
                borderRadius: '4px 0 0 4px', padding: '0 22px', fontWeight: 500, fontSize: '15px', height: 36,
                boxSizing: 'border-box', cursor: 'pointer', boxShadow: 'none', outline: 'none', transition: 'background 0.2s', margin: 0
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={e => (e.currentTarget.style.background = '#fff')}
            >
              뒤로
            </button>
            <button
              onClick={() => setShowCreateFolder(true)}
              style={{
                background: '#fff', color: '#222', border: '1px solid #d1d5db', borderRight: 'none',
                borderRadius: 0, padding: '0 22px', fontWeight: 500, fontSize: '15px', height: 36,
                boxSizing: 'border-box', cursor: 'pointer', boxShadow: 'none', outline: 'none', transition: 'background 0.2s', margin: 0
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={e => (e.currentTarget.style.background = '#fff')}
            >
              새 폴더
            </button>
            <button
              onClick={handleUploadBtn}
              style={{
                background: '#fff', color: '#222', border: '1px solid #d1d5db', borderRight: 'none',
                borderRadius: 0, padding: '0 22px', fontWeight: 500, fontSize: '15px', height: 36,
                boxSizing: 'border-box', cursor: 'pointer', boxShadow: 'none', outline: 'none', transition: 'background 0.2s', margin: 0
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={e => (e.currentTarget.style.background = '#fff')}
            >
              업로드
            </button>
            <button
              onClick={handleMove}
              disabled={selected.length === 0}
              style={{
                background: '#fff', color: '#222', border: '1px solid #d1d5db', borderRight: 'none',
                borderRadius: 0, padding: '0 22px', fontWeight: 500, fontSize: '15px', height: 36,
                boxSizing: 'border-box', cursor: selected.length ? 'pointer' : 'not-allowed', opacity: selected.length ? 1 : 0.6,
                boxShadow: 'none', outline: 'none', transition: 'background 0.2s', margin: 0
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={e => (e.currentTarget.style.background = '#fff')}
            >
              이동
            </button>
            <button
              onClick={() => handleDelete()}
              disabled={selected.length === 0}
              style={{
                background: '#fff', color: '#222', border: '1px solid #d1d5db',
                borderRadius: '0 4px 4px 0', padding: '0 22px', fontWeight: 500, fontSize: '15px', height: 36,
                boxSizing: 'border-box', cursor: selected.length ? 'pointer' : 'not-allowed', opacity: selected.length ? 1 : 0.6,
                boxShadow: 'none', outline: 'none', transition: 'background 0.2s', margin: 0
              }}
              onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseOut={e => (e.currentTarget.style.background = '#fff')}
            >
              삭제
            </button>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={handleGoHome}
            style={{
              background: '#fff', color: '#222', border: '1px solid #d1d5db',
              borderRadius: 6, padding: '6px 16px', fontWeight: 500, fontSize: '14px', height: 34,
              boxSizing: 'border-box', cursor: 'pointer', boxShadow: 'none', outline: 'none', transition: 'background 0.2s', margin: 0
            }}
            onMouseOver={e => (e.currentTarget.style.background = '#f3f4f6')}
            onMouseOut={e => (e.currentTarget.style.background = '#fff')}
          >
            홈
          </button>
          <input
            type="text"
            placeholder="검색(파일/폴더 이름)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginLeft: 12, padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6 }}
          />
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleUpload} />
        </div>

        {/* 브레드크럼 */}
        <div style={{ padding: '8px 12px', color: '#374151' }}>
          {breadcrumb.map((b, i) => (
            <span key={b.prefix}>
              {i > 0 && ' / '}
              <span style={{ cursor: 'pointer', color: b.prefix === currentPrefix ? '#1976d2' : '#374151', fontWeight: b.prefix === currentPrefix ? 700 : 500 }} onClick={() => setCurrentPrefix(b.prefix)}>
                {i === 0 ? '공유파일함' : b.name}
              </span>
            </span>
          ))}
        </div>

        {/* 목록 */}
        <div style={{ flex: 1, overflow: 'auto' }} onContextMenu={(e) => { e.preventDefault(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, item: null }); }} onDragOver={(e) => { e.preventDefault(); }} onDrop={handleDrop}>
          {loading ? (
            <div style={{ padding: 24, color: '#6b7280' }}>로딩 중...</div>
          ) : error ? (
            <div style={{ padding: 24, color: '#ef4444' }}>{error}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 8, width: 40 }}></th>
                  <th style={{ textAlign: 'left', padding: 8, width: '40%' }}>이름</th>
                  <th style={{ textAlign: 'left', padding: 8, width: 120 }}>유형</th>
                  <th style={{ textAlign: 'left', padding: 8, width: 160 }}>수정일</th>
                  <th style={{ textAlign: 'left', padding: 8, width: 120 }}>크기</th>
                  <th style={{ textAlign: 'left', padding: 8, width: 280 }}>동작</th>
                </tr>
              </thead>
              <tbody>
                {items
                  .filter(it => (search ? it.name.toLowerCase().includes(search.toLowerCase()) : true))
                  .sort((a, b) => (a.isFolder === b.isFolder ? a.name.localeCompare(b.name, 'ko') : a.isFolder ? -1 : 1))
                  .map(it => (
                  <tr key={it.id} onContextMenu={(e) => { e.preventDefault(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, item: it }); }}>
                    <td style={{ padding: 8 }}>
                      {!it.isFolder && (
                        <input type="checkbox" checked={selected.includes(it.id)} onChange={e => toggleSelect(it.id, e.target.checked)} />
                      )}
                    </td>
                    <td style={{ padding: 8, maxWidth: 0 }}>
                      {it.isFolder ? (
                        <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%' }} onClick={() => setCurrentPrefix(it.id)}>
                          📁 {it.name}
                        </span>
                      ) : (
                        <span style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%' }} onClick={() => handlePreview(it.id, it.name)}>{it.name}</span>
                      )}
                    </td>
                    <td style={{ padding: 8 }}>{it.isFolder ? '폴더' : (it.mimeType || '파일')}</td>
                    <td style={{ padding: 8 }}>{it.modifiedTime ? new Date(it.modifiedTime).toLocaleString() : '-'}</td>
                    <td style={{ padding: 8 }}>{it.isFolder ? '-' : formatBytes(it.size)}</td>
                    <td style={{ padding: 8 }}>
                      {!it.isFolder && (
                        <div style={{ display: 'inline-flex', gap: 0 }}>
                          <button
                            onClick={() => handlePreview(it.id, it.name)}
                            style={segBtnLeft}
                            onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            미리보기
                          </button>
                          <button
                            onClick={() => handleDownload(it.id)}
                            style={segBtnMid}
                            onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            다운로드
                          </button>
                          <button
                            onClick={() => handleDelete([it.id])}
                            style={segBtnRight}
                            onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            삭제
                          </button>
                        </div>
                      )}
                      {it.isFolder && (
                        <button
                          onClick={() => { if (window.confirm('폴더 및 하위 전체를 삭제하시겠습니까?')) { handleDelete([it.id]); } }}
                          style={segBtnSingle}
                          onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseOut={e => e.currentTarget.style.background = '#fff'}
                        >
                          폴더 삭제
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 컨텍스트 메뉴 */}
        {contextMenu.visible && (
          <div
            onClick={() => setContextMenu({ ...contextMenu, visible: false })}
            style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
          >
            <div
              style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x, background: '#fff', border: '1px solid #e5e7eb', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', borderRadius: 6, minWidth: 160 }}
              onClick={(e) => e.stopPropagation()}
            >
              {contextMenu.item ? (
                <>
                  {contextMenu.item.isFolder ? (
                    <div style={cmItemStyle} onClick={() => { setCurrentPrefix(contextMenu.item!.id); setContextMenu({ ...contextMenu, visible: false }); }}>열기</div>
                  ) : (
                    <>
                      <div style={cmItemStyle} onClick={() => { handlePreview(contextMenu.item!.id, contextMenu.item!.name); setContextMenu({ ...contextMenu, visible: false }); }}>미리보기</div>
                      <div style={cmItemStyle} onClick={() => { handleDownload(contextMenu.item!.id); setContextMenu({ ...contextMenu, visible: false }); }}>다운로드</div>
                    </>
                  )}
                  <div style={cmItemStyle} onClick={() => { setSelected([contextMenu.item!.id]); handleMove(); setContextMenu({ ...contextMenu, visible: false }); }}>이동</div>
                  <div style={{ ...cmItemStyle, color: '#d32f2f' }} onClick={() => { setSelected([contextMenu.item!.id]); handleDelete(); setContextMenu({ ...contextMenu, visible: false }); }}>삭제</div>
                </>
              ) : (
                <>
                  <div style={cmItemStyle} onClick={() => { setShowCreateFolder(true); setContextMenu({ ...contextMenu, visible: false }); }}>새 폴더</div>
                  <div style={cmItemStyle} onClick={() => { handleUploadBtn(); setContextMenu({ ...contextMenu, visible: false }); }}>업로드</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 새 폴더 모달(간단버전) */}
      {showCreateFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowCreateFolder(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', padding: 20, borderRadius: 8, minWidth: 280 }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>새 폴더</div>
            <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #e5e7eb', borderRadius: 6 }} placeholder="폴더 이름" />
            <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateFolder(false)}>취소</button>
              <button onClick={handleCreateFolder} style={{ background: '#1976d2', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6 }}>생성</button>
            </div>
          </div>
        </div>
      )}
      {/* 미리보기 모달 */}
      {preview?.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 16, maxWidth: '90vw', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 700 }}>{preview.name}</div>
              <button onClick={() => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); }}>닫기</button>
            </div>
            {preview.contentType.startsWith('image/') ? (
              <img src={preview.url} alt={preview.name} style={{ maxWidth: '80vw', maxHeight: '75vh' }} />
            ) : preview.contentType.startsWith('text/') ? (
              <iframe src={preview.url} title="preview" style={{ width: '80vw', height: '75vh', border: 'none' }} />
            ) : (
              <div style={{ color: '#666' }}>미리보기를 지원하지 않는 형식입니다. 다운로드를 이용해 주세요.</div>
            )}
          </div>
        </div>
      )}
      {/* 토스트 */}
      {toast?.open && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#111827', color: '#fff', padding: '10px 14px', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.25)' }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default StorageExplorerPage;

// 컨텍스트 메뉴 아이템 스타일
const cmItemStyle: React.CSSProperties = {
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: 14,
  borderBottom: '1px solid #f3f4f6'
};

// 버튼 스타일
const btn: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  border: '1px solid #e5e7eb',
  background: '#fff',
  color: '#374151',
  cursor: 'pointer'
};
const btnPrimary: React.CSSProperties = {
  background: '#1976d2',
  color: '#fff',
  border: '1px solid #1976d2'
};
const btnDanger: React.CSSProperties = {
  background: '#ef4444',
  color: '#fff',
  border: '1px solid #ef4444'
};
const btnGhost: React.CSSProperties = {
  background: '#f9fafb',
  color: '#111827'
};

// 세그먼트 버튼 스타일 (통합/정산일보와 동일 톤)
const segBase: React.CSSProperties = {
  background: '#fff',
  color: '#222',
  border: '1px solid #d1d5db',
  padding: '0 16px',
  height: 32,
  fontSize: 14,
  fontWeight: 500,
  boxSizing: 'border-box',
  cursor: 'pointer',
  boxShadow: 'none',
  outline: 'none',
  transition: 'background 0.2s',
  margin: 0
};
const segBtnLeft: React.CSSProperties = { ...segBase, borderRight: 'none', borderRadius: '4px 0 0 4px' };
const segBtnMid: React.CSSProperties = { ...segBase, borderRight: 'none', borderRadius: 0 };
const segBtnRight: React.CSSProperties = { ...segBase, borderRadius: '0 4px 4px 0' };
const segBtnSingle: React.CSSProperties = { ...segBase, borderRadius: 6, padding: '0 18px' };

// 좌측 트리 뷰 컴포넌트
function TreeView({ node, onToggle, onSelect, currentPrefix }: { node: TreeNode; onToggle: (n: TreeNode) => void; onSelect: (p: string) => void; currentPrefix: string }) {
  return (
    <div>
      <TreeRow node={node} root onToggle={onToggle} onSelect={onSelect} currentPrefix={currentPrefix} />
    </div>
  );
}

function TreeRow({ node, root, onToggle, onSelect, currentPrefix }: { node: TreeNode; root?: boolean; onToggle: (n: TreeNode) => void; onSelect: (p: string) => void; currentPrefix: string }) {
  return (
    <div style={{ marginLeft: root ? 0 : 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onContextMenu={(e) => {
        e.preventDefault();
        const menu = document.createElement('div');
        menu.style.position = 'fixed';
        menu.style.top = `${e.clientY}px`;
        menu.style.left = `${e.clientX}px`;
        menu.style.background = '#fff';
        menu.style.border = '1px solid #e5e7eb';
        menu.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
        menu.style.borderRadius = '6px';
        menu.style.minWidth = '160px';
        menu.style.zIndex = '9999';
        const mk = (label: string, fn: () => void, danger?: boolean) => {
          const item = document.createElement('div');
          item.textContent = label;
          item.style.padding = '8px 12px';
          item.style.cursor = 'pointer';
          item.style.borderBottom = '1px solid #f3f4f6';
          if (danger) item.style.color = '#d32f2f';
          item.onclick = () => { fn(); document.body.removeChild(menu); };
          menu.appendChild(item);
        };
        mk('열기', () => onSelect(node.prefix));
        mk('새 폴더', () => { const name = window.prompt('새 폴더 이름'); if (name) { (async () => {
          try {
            await axios.post('/api/drive/create-folder', { name: name.trim(), parentId: node.prefix });
            onToggle(node); // 접었다가 다시 열게 하여 갱신
            onToggle(node);
          } catch { alert('폴더 생성 실패'); }
        })(); } });
        mk('이동', () => { const target = window.prompt('이 폴더를 어디로 이동할까요? (prefix)'); if (target !== null) { (async () => {
          try { await axios.post('/api/drive/move', { fileId: node.prefix, targetParentId: target || '' }); onSelect(target || ''); } catch { alert('이동 실패'); }
        })(); } });
        mk('삭제', () => { if (window.confirm('폴더 및 하위 전체를 삭제하시겠습니까?')) { (async () => {
          try { await axios.delete('/api/drive/delete', { params: { id: node.prefix } }); onSelect(node.prefix.split('/').slice(0, -2).join('/') + (node.prefix.split('/').length > 2 ? '/' : '')); } catch { alert('삭제 실패'); }
        })(); } }, true);
        const closer = () => { if (document.body.contains(menu)) document.body.removeChild(menu); document.removeEventListener('click', closer); };
        document.addEventListener('click', closer);
        document.body.appendChild(menu);
      }}>
        <span style={{ width: 14, textAlign: 'center', cursor: 'pointer' }} onClick={() => onToggle(node)}>{node.expanded ? '▾' : '▸'}</span>
        <span onClick={() => onSelect(node.prefix)} style={{ cursor: 'pointer', color: node.prefix === currentPrefix ? '#1976d2' : '#374151', fontWeight: node.prefix === currentPrefix ? 700 : 500 }}>{node.name}</span>
      </div>
      {node.expanded && (
        <div style={{ marginTop: 4 }}>
          {node.loading && <div style={{ color: '#9ca3af', paddingLeft: 18 }}>로딩...</div>}
          {!node.loading && node.children && node.children.map((c) => (
            <TreeRow key={c.prefix} node={c} onToggle={onToggle} onSelect={onSelect} currentPrefix={currentPrefix} />
          ))}
        </div>
      )}
    </div>
  );
}
